import {DateTimeFormat} from '@hconnect/common/components/dateTime/DateTimeFormat'
import {DateTime, SapDataDto, SapStatusCode} from '@hconnect/common/types'
import {Typography} from '@mui/material'
import moment, {Moment} from 'moment-timezone'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../../consts'

export const hasMaintenanceNotificationCreationTimedOut = (item: SapDataDto, now: Moment) =>
  !item.sapMaintenanceNotificationId &&
  Boolean(item.lastMaintenanceNotificationCreationStart) &&
  (!item.sapStatusCode || item.sapStatusCode === SapStatusCode.SUBMITTED) &&
  (item.lastMaintenanceNotificationCreationStart
    ? moment(item.lastMaintenanceNotificationCreationStart).clone().add(10, 'minutes').isBefore(now)
    : undefined)

type InfoProps = {
  label?: string
  timezone: string
  date?: DateTime | undefined
  userName?: string
  'data-test-id'?: string
  component?: React.ElementType
}

export const Info: React.FC<InfoProps> = ({
  label,
  userName,
  timezone,
  date,
  'data-test-id': dataTestId,
  component
}) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)

  return (
    <Typography
      component={component ?? 'p'}
      gutterBottom
      variant="caption"
      data-test-id={dataTestId}
    >
      {label && `${label} `}
      {userName && date && (
        <>
          {userName} {t('dalogActions.happenOn')} <DateTimeFormat date={date} timezone={timezone} />
        </>
      )}
    </Typography>
  )
}

export const DalogSapMaintenanceNotificationDetails = ({
  item,
  notificationFailed,
  timezone
}: {
  item: SapDataDto
  notificationFailed: boolean | undefined
  timezone: string
}) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const sapMaintenanceNotificationId = item.sapMaintenanceNotificationId
  const hasNotificationCreationInfo =
    item.sapMaintenanceNotificationCreator?.name && item.lastMaintenanceNotificationCreationStart

  return (
    <>
      {(hasMaintenanceNotificationCreationTimedOut(item, moment.tz(timezone)) ||
        notificationFailed) && (
        <>
          <Typography
            component="p"
            pt={0.5}
            variant="caption"
            color="error"
            data-test-id="event-info-sap-notification-error"
          >
            {t('dalogActions.maintenanceNotificationCreationTimeout')}{' '}
          </Typography>
          {hasNotificationCreationInfo && (
            <Info
              component="span"
              label={t('dalogActions.sentBy')}
              timezone={timezone}
              userName={item.sapMaintenanceNotificationCreator?.name}
              date={item.lastMaintenanceNotificationCreationStart}
              data-test-id="event-info-sap-notification-error-creator-info"
            />
          )}
        </>
      )}
      {sapMaintenanceNotificationId && (
        <Info
          label={`${t('dalogActions.sapPmNotificationId')}: ${sapMaintenanceNotificationId} ${
            hasNotificationCreationInfo ? t('dalogActions.sentBy') : ''
          }`}
          timezone={timezone}
          data-test-id="event-info-sap-notification-label"
          userName={item.sapMaintenanceNotificationCreator?.name}
          date={item.lastMaintenanceNotificationCreationStart}
        />
      )}
    </>
  )
}
